import { useSyncExternalStore } from "react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { describeAbsoluteTime, describeRelativeTime, type Moment } from "@/time/describe.ts"

/*
 * When something happened, said relatively ("5m ago", "Yesterday", "3 Sept") with the full moment
 * in a tooltip ("29 September 2026, 14:03"). It takes the colour and size of the line it sits in.
 *
 * Every RelativeTime on the page reads one clock that ticks each minute, so "just now" becomes
 * "1m ago" without a reload, and a page of a hundred rows still sets only one timer.
 */

const tick = 60 * 1000

let now = Date.now()
const listeners = new Set<() => void>()
let timer: ReturnType<typeof setInterval> | undefined

function subscribe(listener: () => void) {
  listeners.add(listener)
  if (!timer) {
    now = Date.now()
    timer = setInterval(() => {
      now = Date.now()
      listeners.forEach((each) => each())
    }, tick)
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      clearInterval(timer)
      timer = undefined
    }
  }
}

const currentMinute = () => now

export function RelativeTime({ at }: { at: Moment }) {
  const clock = useSyncExternalStore(subscribe, currentMinute)
  const moment = at instanceof Date ? at : new Date(at)
  // A moment that isn't one, from a malformed answer, says nothing rather than "Invalid Date".
  if (Number.isNaN(moment.getTime())) return null
  return (
    <Tooltip>
      <TooltipTrigger
        render={<time dateTime={moment.toISOString()} className="whitespace-nowrap tabular-nums" />}
      >
        {describeRelativeTime(moment, new Date(clock))}
      </TooltipTrigger>
      <TooltipContent>{describeAbsoluteTime(moment)}</TooltipContent>
    </Tooltip>
  )
}
